import { resolveEmployeeId } from '../../auth/utils/tokenPermissions';
import { ConversationInteractiveButtons, ConversationInteractiveList, ConversationInteractiveListRow, ConversationParticipantRef, Conversation, Message, QuotedMessage } from '../types/Conversation';
import { ConversationClassificationSource, isExternalConversation } from './conversationClassification';

const captionPlaceholderPattern = /^\[(image|photo|picture|video|document|file|audio message|audio|sticker|location|voice|voice message|voice note)\]$/i;

export const isMediaTypePlaceholder = (value?: string): boolean => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return false;
  }
  if (captionPlaceholderPattern.test(trimmed)) {
    return true;
  }
  return /^\[?\s*(image|photo|picture|video|document|file|audio message|audio|sticker|location|voice|voice message|voice note)\s*\]?$/i.test(trimmed);
};

export const isVoiceRecordingName = (value?: string): boolean => /(^|[/\\])voice[-_]?note/i.test((value || '').trim());

export const getMediaPreviewKind = (messageType?: string, content?: string, fileName?: string): string => {
  const type = (messageType || '').trim().toLowerCase();
  if (type && type !== 'text') {
    return type === 'audio message' ? 'audio' : type;
  }
  const text = (content || '').trim().toLowerCase();
  const inner = text.startsWith('[') && text.endsWith(']') ? text.slice(1, -1).trim() : text;
  if (inner === 'image' || inner === 'photo' || inner === 'picture') return 'image';
  if (inner === 'video') return 'video';
  if (inner === 'audio' || inner === 'audio message' || inner === 'voice' || inner === 'voice message' || inner === 'voice note') return 'audio';
  if (inner === 'document' || inner === 'file') return 'document';
  if (inner === 'sticker') return 'sticker';
  if (inner === 'location') return 'location';
  if (isVoiceRecordingName(content) || isVoiceRecordingName(fileName)) return 'audio';
  return type;
};

export const getMediaPreviewIcon = (kind: string): string => {
  switch (kind) {
    case 'image':
    case 'photo':
      return '📷';
    case 'video':
      return '🎬';
    case 'audio':
    case 'voice':
    case 'voice-note':
    case 'voice_note':
    case 'audio message':
      return '🎤';
    case 'document':
    case 'file':
      return '📄';
    case 'location':
      return '📍';
    case 'sticker':
      return '🏷️';
    default:
      return '';
  }
};

export const getMediaPreviewLabel = (kind: string, translate: (key: string) => string): string => {
  switch (kind) {
    case 'image':
    case 'photo':
      return translate('imageMessage');
    case 'video':
      return translate('videoAttachment');
    case 'audio':
    case 'voice':
    case 'voice-note':
    case 'voice_note':
    case 'audio message':
      return translate('audioMessage');
    case 'document':
    case 'file':
      return translate('documentAttachment');
    case 'sticker':
      return translate('stickerMessage');
    case 'location':
      return translate('locationMessage');
    default:
      return '';
  }
};

export const formatMediaListPreview = (
  content: string | undefined,
  messageType: string | undefined,
  fileName: string | undefined,
  translate: (key: string) => string,
): string => {
  const kind = getMediaPreviewKind(messageType, content, fileName);
  const icon = getMediaPreviewIcon(kind);
  const trimmed = (content || '').trim();
  const isAudio = ['audio', 'voice', 'voice-note', 'voice_note', 'audio message'].includes(kind);
  if (trimmed && !isMediaTypePlaceholder(trimmed) && !isVoiceRecordingName(trimmed)) {
    return icon && kind && kind !== 'text' ? `${icon} ${trimmed}` : trimmed;
  }
  const name = (fileName || '').trim();
  if (!isAudio && name && !isVoiceRecordingName(name)) {
    return icon ? `${icon} ${name}` : name;
  }
  const label = getMediaPreviewLabel(kind, translate);
  if (icon && label) {
    return `${icon} ${label}`;
  }
  return label || kind.replace(/_/g, ' ');
};

const getMessageSortTimestamp = (message: Message): number => new Date(message.timestamp).getTime();

const getMessageKey = (messageId: string): string => messageId.trim().toLowerCase();

export const getMessageTypeLabel = (messageType?: string): string => {
  switch ((messageType || '').toLowerCase()) {
    case 'image': return 'Image';
    case 'audio': return 'Audio';
    case 'video': return 'Video';
    case 'document': return 'Document';
    case 'sticker': return 'Sticker';
    case 'location': return 'Location';
    case 'interactive': return 'Interactive';
    case 'event': return 'Event';
    case 'system': return 'System';
    default: return 'Text';
  }
};

const EXTERNAL_REPLY_AUDIT_SUFFIX = 'joined this external conversation on behalf of the company team.';

export const isExternalReplyAuditMessage = (message: Pick<Message, 'messageType' | 'content' | 'sender'>): boolean => {
  const isSystemMessage = message.messageType === 'system' || message.sender.type === 'system';
  return isSystemMessage && message.content.trim().toLowerCase().endsWith(EXTERNAL_REPLY_AUDIT_SUFFIX);
};

export const isOutgoingConversationMessage = (
  message: Pick<Message, 'sender'>,
  meSender?: ConversationParticipantRef | null,
  isExternalConversation = false,
): boolean => {
  const isMe = Boolean(
    meSender
    && message.sender.type === meSender.type
    && idsEqual(message.sender.id, meSender.id),
  );

  return isMe
    || message.sender.type === 'businessSuite'
    || (isExternalConversation && message.sender.type === 'aiAssistant');
};

export const idsEqual = (a?: string, b?: string): boolean => {
  if (!a || !b) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

export const resolveConversationMeSender = (
  conversation: ConversationClassificationSource | null | undefined,
  accessToken?: string | null,
  tenantId?: string | null,
  employeeIdOverride?: string | null,
): ConversationParticipantRef | undefined => {
  const employeeId = (employeeIdOverride?.trim())
    || (accessToken && tenantId ? resolveEmployeeId(accessToken, tenantId) : null);
  if (!employeeId) {
    return undefined;
  }

  const employeeParticipant = conversation?.participants.find(participant => participant.type === 'companyMember' && idsEqual(participant.id, employeeId));
  if (employeeParticipant) {
    return employeeParticipant;
  }

  if (conversation && (isExternalConversation(conversation) || conversation.hasExternalParticipant)) {
    return { type: 'companyMember', id: employeeId };
  }

  return undefined;
};

const getParticipantKey = (participant: ConversationParticipantRef): string => (
  `${participant.type}:${participant.id}`.trim().toLowerCase()
);

const mergeParticipantRefs = (
  existing: ConversationParticipantRef[],
  incoming: ConversationParticipantRef[],
): ConversationParticipantRef[] => {
  const merged = new Map<string, ConversationParticipantRef>();
  existing.forEach(participant => merged.set(getParticipantKey(participant), participant));
  incoming.forEach(participant => merged.set(getParticipantKey(participant), participant));
  return Array.from(merged.values());
};

const mergeMessageReactions = (
  existing: Message['reactions'],
  incoming: Message['reactions'],
): Message['reactions'] => {
  if (Object.keys(incoming).length === 0) {
    return existing;
  }

  const merged = { ...existing };
  Object.entries(incoming).forEach(([emoji, reactors]) => {
    merged[emoji] = mergeParticipantRefs(merged[emoji] || [], reactors);
  });
  return merged;
};

const mergeMessageSnapshot = (existing: Message | undefined, incoming: Message): Message => {
  if (!existing) {
    return incoming;
  }

  return {
    ...existing,
    ...incoming,
    readBy: mergeParticipantRefs(existing.readBy, incoming.readBy),
    reactions: mergeMessageReactions(existing.reactions, incoming.reactions),
    mediaUrl: incoming.mediaUrl ?? existing.mediaUrl,
    mimeType: incoming.mimeType ?? existing.mimeType,
    fileName: incoming.fileName ?? existing.fileName,
    quotedMessageId: incoming.quotedMessageId ?? existing.quotedMessageId,
    quotedMessage: incoming.quotedMessage ?? existing.quotedMessage,
    transcription: incoming.transcription ?? existing.transcription,
    transcriptionUpdatedAt: incoming.transcriptionUpdatedAt ?? existing.transcriptionUpdatedAt,
  };
};

export const upsertAndSortMessages = (messages: Message[], incoming: Message): Message[] => {
  return mergeMessages(messages, [incoming]);
};

export const mergeMessages = (messages: Message[], incomingMessages: Message[]): Message[] => {
  const messageMap = new Map(messages.map(message => [getMessageKey(message.id), message]));

  incomingMessages.forEach(message => {
    const key = getMessageKey(message.id);
    messageMap.set(key, mergeMessageSnapshot(messageMap.get(key), message));
  });

  return Array.from(messageMap.values()).sort((left, right) => getMessageSortTimestamp(left) - getMessageSortTimestamp(right));
};

export const getConversationLastActivityTimestamp = (
  conversation: Pick<Conversation, 'messages' | 'updatedAt'>,
): number => {
  let latestMessageTimestamp = 0;

  conversation.messages.forEach(message => {
    const timestamp = getMessageSortTimestamp(message);
    if (!Number.isNaN(timestamp) && timestamp > latestMessageTimestamp) {
      latestMessageTimestamp = timestamp;
    }
  });

  if (latestMessageTimestamp > 0) {
    return latestMessageTimestamp;
  }

  const updatedAtTimestamp = new Date(conversation.updatedAt).getTime();
  return Number.isNaN(updatedAtTimestamp) ? 0 : updatedAtTimestamp;
};

export const getConversationLastActivityIso = (
  conversation: Pick<Conversation, 'messages' | 'updatedAt'>,
): string => {
  const timestamp = getConversationLastActivityTimestamp(conversation);
  if (timestamp <= 0) {
    return conversation.updatedAt;
  }

  return new Date(timestamp).toISOString();
};

export interface InteractiveMessageSummary {
  bodyText?: string;
  buttonText?: string;
  optionCount?: number;
  buttonCount?: number;
}

export const summarizeInteractiveList = (interactiveList?: ConversationInteractiveList): InteractiveMessageSummary | null => {
  if (!interactiveList) {
    return null;
  }

  const rows = interactiveList.sections.flatMap(section => section.rows || []);
  return {
    bodyText: interactiveList.bodyText?.trim() || undefined,
    buttonText: interactiveList.buttonText?.trim() || undefined,
    optionCount: rows.length > 0 ? rows.length : undefined,
  };
};

export const summarizeInteractiveButtons = (interactiveButtons?: ConversationInteractiveButtons): InteractiveMessageSummary | null => {
  if (!interactiveButtons) {
    return null;
  }

  return {
    bodyText: interactiveButtons.bodyText?.trim() || undefined,
    buttonCount: interactiveButtons.buttons.length > 0 ? interactiveButtons.buttons.length : undefined,
  };
};

export const formatInteractiveSelectionReply = (
  row: Pick<ConversationInteractiveListRow, 'title' | 'description'>,
): string => {
  const title = (row.title || '').trim();
  const description = row.description?.trim();
  if (!description) {
    return title;
  }

  if (!title) {
    return description;
  }

  return `${title}\n${description}`;
};

/** Hides trailing " (payload)" / " (btn_…)" suffixes from interactive tap replies. */
export const stripInteractiveSelectionPayloadSuffix = (value?: string | null): string => {
  const normalized = (value || '').trim();
  if (!normalized) {
    return '';
  }

  const stripped = normalized.replace(/\s+\(([^()\s][^()]*)\)\s*$/, '').trim();
  return stripped || normalized;
};

export const parseInteractiveMessageSummary = (content?: string): InteractiveMessageSummary | null => {
  const normalized = stripInteractiveSelectionPayloadSuffix(content);
  if (!normalized) {
    return null;
  }

  const parts = normalized.split('|').map(part => part.trim()).filter(Boolean);
  if (parts.length === 0) {
    return null;
  }

  let bodyText: string | undefined;
  let buttonText: string | undefined;
  let optionCount: number | undefined;
  let buttonCount: number | undefined;

  parts.forEach(part => {
    if (/^button\s*:/i.test(part)) {
      buttonText = part.replace(/^button\s*:/i, '').trim() || undefined;
      return;
    }

    if (/^options\s*:/i.test(part)) {
      const parsed = Number(part.replace(/^options\s*:/i, '').trim());
      optionCount = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
      return;
    }

    if (/^buttons\s*:/i.test(part)) {
      const parsed = Number(part.replace(/^buttons\s*:/i, '').trim());
      buttonCount = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
      return;
    }

    if (!bodyText) {
      bodyText = part;
    }
  });

  if (!bodyText && !buttonText && !optionCount && !buttonCount) {
    return null;
  }

  return { bodyText, buttonText, optionCount, buttonCount };
};

export const getInteractiveMessageSummary = (message: Pick<Message, 'content' | 'interactiveList' | 'interactiveButtons'>): InteractiveMessageSummary | null => {
  return summarizeInteractiveList(message.interactiveList)
    ?? summarizeInteractiveButtons(message.interactiveButtons)
    ?? parseInteractiveMessageSummary(message.content);
};

const getQuoteFallbackLabel = (messageType?: string): string => {
  const label = getMessageTypeLabel(messageType);
  return `[${label}]`;
};

export const getQuotedMessageSnippet = (message: Pick<QuotedMessage, 'content' | 'messageType' | 'fileName'>): string => {
  const content = (message.content || '').trim();
  if (content && !isMediaTypePlaceholder(content) && !isVoiceRecordingName(content)) {
    return content;
  }

  const fileName = (message.fileName || '').trim();
  const kind = getMediaPreviewKind(message.messageType, message.content, message.fileName);
  const isAudio = ['audio', 'voice', 'voice-note', 'voice_note', 'audio message'].includes(kind);
  if (fileName && !isAudio && !isVoiceRecordingName(fileName)) {
    return fileName;
  }

  return getQuoteFallbackLabel(kind || message.messageType);
};

export const truncateMessageSnippet = (value: string, maxLength = 96): string => {
  const normalized = value.trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};
